import { useMemo } from 'react';

import { useCageController_Context } from '@/services/hooks';
import type { CageContextResponseDto } from '@/services/types';

import type { DomainQueryOptions } from '../shared/types';

import type { UseGetCageContextResult } from './context.types';
import { mapCageContextToDomain } from './mappers';

/**
 * Context query params. Omitted entirely when the page is not about one fish group, so the
 * key stays the pen-wide one.
 */
export function cageContextQueryParams(fishGroupId?: number): { fishGroupId: number } | undefined {
    return fishGroupId != null ? { fishGroupId } : undefined;
}

/**
 * The cage's context. With `fishGroupId`, its `dataAvailability` is the fish group's; every other
 * block still describes the pen. Without one the request carries no query params, as it always did.
 */
export const useGetCageContext = (
    cageId: number,
    params: { fishGroupId?: number } = {},
    options?: DomainQueryOptions<CageContextResponseDto>
): UseGetCageContextResult => {
    const contextQuery = useCageController_Context(
        cageId,
        cageContextQueryParams(params.fishGroupId),
        options
    );

    const cageContext = useMemo(() => {
        const payload = contextQuery.data?.data;
        return payload ? mapCageContextToDomain(payload) : null;
    }, [contextQuery.data?.data]);

    return {
        ...contextQuery,
        isLoading: contextQuery.isPending,
        data: cageContext,
    };
};
